import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { Typography } from 'antd';
import { memo } from 'react';
import { PerformanceName } from 'services/api/dashboard';
import { useDashboardFilter } from '../Filter/useFilter';
import { useGetRefundTypeLabel } from '../OrderWastage/RecoverTable';
import TrendHeaderBg1 from './assets/trendHeaderBg1.png';
import TrendHeaderBg2 from './assets/trendHeaderBg2.png';
import useDrawerContext from './hooks/useDrawer';
import { T, TFnType } from '@tolgee/react';

const HeaderParagraph = styled(Typography.Paragraph)`
  position: relative;
  padding-left: 16px;
  min-width: 272px;
  color: #454b58;
  word-break: break-all;
  line-height: 1;
  [data-color-mode='dark'] & {
    color: var(--fett-text-primary);
  }
`;
const HeaderContainer = styled.div`
  background: #d8d8d8;
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 80px;
  background: url(${TrendHeaderBg2}) no-repeat right center,
    linear-gradient(180deg, #e2ebff 0%, #e8effe 100%);
  border-radius: 4px;
  background-size: auto 100%;
  [data-color-mode='dark'] & {
    background: linear-gradient(180deg, #262b33 0%, #1d222a 100%);
  }
  ${HeaderParagraph} {
    margin-bottom: 0;
  }
`;
const TrendHeaderIcon = styled.div`
  width: 80px;
  height: 56px;
  background: url(${TrendHeaderBg1}) no-repeat;
  background-size: cover;
`;

const PERFORMANCE_LABEL_MAP: Record<PerformanceName, Util.PickByType<string, string>> = {
  // 订单概况页面
  REORDER_RATIO: '复购率',
  // 销售转化页面
  PRE_SALE_CONSULT_CONVERSION_RATE: '售前咨询转化率',
  OVERALL_CONSULT_CONVERSION_RATE: '整体咨询转化率',
  ORDER_REMINDER_CONVERSION_RATE: '催单后下单买家比例',
  PAYMENT_REMINDER_CONVERSION_RATE: '催付款后付款比例',

  // 非COD订单损耗
  NOT_COD_OVERALL_LOSS_RATE: '损耗率',
  NOT_COD_ORDER_TO_PAYMENT_LOSS_RATE: '下单-付款-损耗率',
  NOT_COD_PAYMENT_TO_READY_TO_SHIP_LOSS_RATE: '付款-卖家点击发货-损耗率',
  NOT_COD_PAYMENT_TO_PACKED_LOSS_RATE: '付款-打包-损耗率',
  NOT_COD_PACKED_TO_READY_TO_SHIP_LOSS_RATE: '打包-卖家点击发货-损耗率',
  NOT_COD_READY_TO_SHIP_TO_SHIPPED_LOSS_RATE: '卖家点击发货-揽收-损耗率',
  NOT_COD_SHIPPED_TO_RECEIVED_LOSS_RATE: '揽收-买家收货-损耗率',
  NOT_COD_RECEIVED_TO_CLOSED_LOSS_RATE: '买家收货-订单结束-损耗率',

  // COD订单损耗
  COD_OVERALL_LOSS_RATE: '损耗率',
  COD_ORDER_TO_CONFIRMED_LOSS_RATE: '下单-订单确认-损耗率',
  COD_CONFIRMED_TO_READY_TO_SHIP_LOSS_RATE: '订单确认-卖家点击发货-损耗率',
  COD_CONFIRMED_TO_PACKED_LOSS_RATE: '订单确认-打包-损耗率',
  COD_PACKED_TO_READY_TO_SHIP_LOSS_RATE: '打包-卖家点击发货-损耗率',
  COD_READY_TO_SHIP_TO_SHIPPED_LOSS_RATE: '卖家点击发货-揽收-损耗率',
  COD_SHIPPED_TO_RECEIVED_LOSS_RATE: '揽收-买家收货-损耗率',
  COD_RECEIVED_TO_CLOSED_LOSS_RATE: '买家收货-订单结束-损耗率',

  // 评价数据页面
  BAD_COMMENT_RATE: '中差评率',
  PRAISE_REMINDER_CONVERSION_RATE: '自动催好评成功率',
  FIX_COMMENT_REMINDER_CONVERSION_RATE: '中差评挽回率',
};

/**
 * 翻译原因
 * 目前只支持shopee平台
 * @param platform
 * @param t
 * @param reason
 * @returns
 */
export function translateReason(platform: HeaderProps['platform'], t: TFnType, reason: string) {
  if (platform === 'SHOPEE') {
    const key = `ViewDashboard.SHOPEE.${reason}` as any;
    return t(key, { defaultValue: reason });
  }
  return reason;
}

type ParamsType = Pick<ReturnType<typeof useDrawerContext>, 'params'>;

interface HeaderProps {
  params: ParamsType[keyof ParamsType];
  platform: ReturnType<typeof useDashboardFilter>['platform'];
  percent: string;
  industryLength: string;
  all: boolean;
}

function Header(props: HeaderProps) {
  const { platform, params, percent, industryLength, all } = props;
  const { t, getRefundTypeLabel } = useGetRefundTypeLabel();
  const i18nKey = all
    ? 'ViewDashboard.已选店铺在x个行业中，y平均领先z店铺'
    : 'ViewDashboard.已选店铺在当前行业中，y平均领先z店铺';

  const { cancelType, performanceName, reason } = params;

  return (
    <HeaderContainer>
      <TrendHeaderIcon />
      <HeaderParagraph>
        <T
          keyName={i18nKey}
          params={{
            x: industryLength,
            y: performanceName
              ? t(`ViewDashboard.${PERFORMANCE_LABEL_MAP[performanceName]}`)
              : `${getRefundTypeLabel(cancelType!)}-${translateReason(platform, t, reason!)}-${t(
                  'ViewDashboard.订单挽回率',
                )}`,
            z: percent,
            comx: <div />,
            comy: (
              <span
                className={css`
                  color: #3366ff;
                  font-size: 25px;
                  margin: 0 4px;
                `}
              />
            ),
          }}
        />
      </HeaderParagraph>
    </HeaderContainer>
  );
}

export default memo(Header);
